/**
 * The live voice translator's languages (SEO_BRIEF_10 section 2.3): the twenty that both the
 * browser's speech recognition and the translation handle well, each with what the three parts
 * of the translator need of it.
 *
 *   code         the language as the translation takes it, and as the usage events and the
 *                server's `/api/live-translate` name it (server/pipeline/translate.py
 *                TRANSLATE_TARGETS): `zh` is Simplified Chinese, the Mandarin spoken here
 *   recognition  the speech-recognition locale (`SpeechRecognition.lang`), and the regional
 *                tags a browser set to one of them listens in instead (`recognitionLocale`)
 *   voices       the `speechSynthesis` voice tags read aloud with, best first (`pickVoice` in
 *                speech-out.ts): `zh` never takes a Cantonese voice
 *
 * The picker's names are the page's language's, from `Intl.DisplayNames` (`languageName`), with
 * Chinese named "Chinese (Mandarin)" as the brief does. tools/_live_translator.html lists the
 * same codes in the same order, each named in its own language for the first paint
 * (web/test/live-translator.test.ts holds the two equal).
 */

import { type PageLang, t } from '../i18n.ts';

export interface LiveLanguage {
  readonly code: string;
  /** The recognition locale this language listens in by default. */
  readonly recognition: string;
  /** Other recognition locales, taken when the browser's own language is one of them. */
  readonly regions: readonly string[];
  /** Voice tags for read-aloud, best first; any voice of the same base code comes after. */
  readonly voices: readonly string[];
}

const lang = (
  code: string,
  recognition: string,
  regions: readonly string[] = [],
  voices: readonly string[] = [recognition],
): LiveLanguage => ({ code, recognition, regions, voices });

/** The brief's twenty, in its order. */
export const LIVE_LANGUAGES: readonly LiveLanguage[] = [
  lang(
    'en',
    'en-US',
    ['en-GB', 'en-AU', 'en-CA', 'en-IN', 'en-IE', 'en-NZ', 'en-ZA'],
    ['en-US', 'en-GB'],
  ),
  lang(
    'es',
    'es-MX',
    ['es-ES', 'es-US', 'es-AR', 'es-CO', 'es-CL', 'es-PE'],
    ['es-MX', 'es-US', 'es-ES'],
  ),
  lang('pt', 'pt-BR', ['pt-PT'], ['pt-BR', 'pt-PT']),
  lang('fr', 'fr-FR', ['fr-CA', 'fr-BE', 'fr-CH'], ['fr-FR', 'fr-CA']),
  lang('de', 'de-DE', ['de-AT', 'de-CH'], ['de-DE']),
  lang('it', 'it-IT'),
  lang('ja', 'ja-JP'),
  lang('ko', 'ko-KR'),
  // Mandarin. `cmn-Hans-CN` is Chrome's own name for it; zh-CN is the tag every browser takes.
  lang('zh', 'zh-CN', [], ['zh-CN', 'cmn-Hans-CN', 'cmn-CN', 'zh-TW', 'cmn-Hant-TW']),
  lang('hi', 'hi-IN'),
  lang('ar', 'ar-SA', ['ar-EG', 'ar-AE', 'ar-JO', 'ar-MA'], ['ar-SA', 'ar-EG', 'ar-AE']),
  lang('ru', 'ru-RU'),
  lang('tr', 'tr-TR'),
  lang('id', 'id-ID'),
  lang('vi', 'vi-VN'),
  lang('nl', 'nl-NL', ['nl-BE'], ['nl-NL', 'nl-BE']),
  lang('pl', 'pl-PL'),
  lang('sv', 'sv-SE'),
  lang('th', 'th-TH'),
  lang('uk', 'uk-UA'),
];

export const LIVE_CODES: readonly string[] = LIVE_LANGUAGES.map((l) => l.code);

const BY_CODE: ReadonlyMap<string, LiveLanguage> = new Map(LIVE_LANGUAGES.map((l) => [l.code, l]));

/** The language for `code`, or null for one the picker does not list. */
export function liveLanguage(code: string | null | undefined): LiveLanguage | null {
  return typeof code === 'string' ? (BY_CODE.get(code) ?? null) : null;
}

/** A BCP 47 tag's language code, lower case: `pt-BR` → `pt`, `cmn-Hans-CN` → `zh`. */
export function baseCode(tag: string | null | undefined): string {
  if (typeof tag !== 'string') return '';
  const head = tag.trim().replace(/_/g, '-').split('-')[0]?.toLowerCase() ?? '';
  return head === 'cmn' ? 'zh' : head;
}

/**
 * The recognition locale for `code`: the browser's own tag when it is one of the language's
 * regions (an English (UK) browser listens in en-GB), else the language's default.
 */
export function recognitionLocale(code: string, browser: readonly string[] = []): string {
  const language = liveLanguage(code);
  if (!language) return 'en-US';
  for (const tag of browser) {
    const want = tag.replace(/_/g, '-').toLowerCase();
    const hit = [language.recognition, ...language.regions].find((r) => r.toLowerCase() === want);
    if (hit) return hit;
  }
  return language.recognition;
}

/** The first of the browser's languages the picker lists, or null. */
export function browserLanguage(browser: readonly string[]): string | null {
  for (const tag of browser) {
    const code = baseCode(tag);
    if (BY_CODE.has(code)) return code;
  }
  return null;
}

export interface LanguagePair {
  /** The speaker's language ("I speak"), and the first person's in a conversation. */
  readonly a: string;
  /** The language translated into ("Translate to"), and the second person's. */
  readonly b: string;
}

/**
 * The pair a visit starts with (section 2.3). A translated page names its own language
 * (`sourceDefault`) and English. An English page has none: the browser's language and
 * English, or English and Spanish when the browser is set to English or to a language the
 * picker does not list.
 */
export function defaultPair(
  sourceDefault: string | null | undefined,
  targetDefault: string | null | undefined,
  browser: readonly string[],
): LanguagePair {
  const target = liveLanguage(targetDefault) ? (targetDefault as string) : 'en';
  const source = liveLanguage(sourceDefault)
    ? (sourceDefault as string)
    : (browserLanguage(browser) ?? 'en');
  if (source !== target) return { a: source, b: target };
  return { a: source, b: source === 'en' ? 'es' : 'en' };
}

/* ── names ─────────────────────────────────────────────────────────────── */

/** English names, for a browser without `Intl.DisplayNames`. */
const ENGLISH_NAMES: Readonly<Record<string, string>> = {
  en: 'English',
  es: 'Spanish',
  pt: 'Portuguese',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  ja: 'Japanese',
  ko: 'Korean',
  zh: 'Chinese',
  hi: 'Hindi',
  ar: 'Arabic',
  ru: 'Russian',
  tr: 'Turkish',
  id: 'Indonesian',
  vi: 'Vietnamese',
  nl: 'Dutch',
  pl: 'Polish',
  sv: 'Swedish',
  th: 'Thai',
  uk: 'Ukrainian',
};

/** The picker's label for Chinese, in the page's language (lib/i18n.ts). */
export const MANDARIN = 'Chinese (Mandarin)';

/**
 * Names `Intl.DisplayNames` gets wrong for a page's language, set to the words the page copy
 * uses (Indonesian gives the Polish autonym and "Tionghoa"; Vietnamese gives "Italy").
 * `zh` here is its name in a sentence; the picker's Chinese is `MANDARIN` in lib/i18n.ts.
 */
const NAME_OVERRIDES: Readonly<Partial<Record<PageLang, Readonly<Record<string, string>>>>> = {
  id: { pl: 'Polandia', zh: 'Mandarin' },
  vi: { it: 'tiếng Ý', zh: 'tiếng Trung (Quan Thoại)' },
};

function displayName(code: string, page: PageLang): string {
  const override = NAME_OVERRIDES[page]?.[code];
  if (override) return override;
  try {
    const names = new Intl.DisplayNames([page], { type: 'language', fallback: 'none' });
    const name = names.of(code);
    if (name) return name;
  } catch {
    /* no Intl.DisplayNames in this browser */
  }
  return ENGLISH_NAMES[code] ?? code;
}

/**
 * `code` as a picker option names it, in the page's language: "Spanish", "Inglés", "영어",
 * "Chinese (Mandarin)". The first letter is a capital, as a menu item's is.
 */
export function languageName(code: string, page: PageLang = 'en'): string {
  if (code === 'zh') return t(MANDARIN, page);
  const name = displayName(code, page);
  return name.charAt(0).toLocaleUpperCase(page) + name.slice(1);
}

/**
 * `code` as a sentence names it, in the page's language: "inglés", "tiếng Anh", "Spanish"
 * (server/app/language_names.py `name_in_text` is the same rule).
 */
export function languageInText(code: string, page: PageLang = 'en'): string {
  const name = displayName(code, page);
  if (page === 'vi') return name.charAt(0).toLocaleLowerCase('vi') + name.slice(1);
  return name;
}
